import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { LucideIcon } from 'lucide-react'
import type { Tab } from '../../hooks/multiWindow'
import { useActiveTab, useTabActions } from '../../contexts/TabManagerContext'
import { DormantIcon } from '../../components/icons/DormantIcon'
import Overview from '../Overview'
import PersonalToday from '../PersonalToday'
import WorkToday from '../WorkToday'
import {
  TaskTodayView,
  TaskWeekView,
  HabitsView,
  RecurringView,
  AreaView,
  TutorialProjectPage,
  ProjectPage,
} from '../../features/taskManagement'
import { GoalsView } from '../../features/goals'
import { QuickNotesPage } from '../../features/taskManagement/pages/QuickNotesPage'
import { UpcomingPage } from '../../features/taskManagement/pages/UpcomingPage'
import { DormantPage } from '../../features/taskManagement/pages/DormantPage'

// Module-level regex constants — avoids recompiling on every render. Each
// pattern is anchored so partial paths don't match accidentally.
const LIFE_SPACE = '(personal|work|home)'
// Legacy "Im Blick" path. Tabs still sitting on `/tasks` after the
// Bereit/Im-Blick refactor are redirected to `/dormant` by
// LegacyTasksRedirect below.
const LEGACY_TASKS_RE = new RegExp(`^/home/${LIFE_SPACE}/tasks$`)
const DORMANT_RE = new RegExp(`^/home/${LIFE_SPACE}/dormant$`)
const QUICK_NOTES_RE = new RegExp(`^/home/${LIFE_SPACE}/quick-notes$`)
const UPCOMING_RE = new RegExp(`^/home/${LIFE_SPACE}/upcoming$`)
const GOALS_RE = new RegExp(`^/home/${LIFE_SPACE}/goals$`)
const TUTORIAL_RE = new RegExp(`^/home/${LIFE_SPACE}/area/Erste-Schritte-in-HappyTask$`)
const PROJECT_RE = new RegExp(`^/home/${LIFE_SPACE}/project/(.+)$`)
const AREA_RE = new RegExp(`^/home/${LIFE_SPACE}/area/(.+)$`)
// Titles the legacy `/tasks` tab might have carried before the refactor.
// Matching titles get renamed to "Ruhend" on redirect; custom-renamed titles
// are left untouched.
const LEGACY_TASKS_AUTO_RENAME_TITLES = new Set([
  'Im Blick',
  'In Focus',
  'Bereit',
  'Ready',
])

type PageLifeSpace = 'personal' | 'work' | 'home'

interface LegacyTasksRedirectProps {
  tab: Tab
  lifeSpace: PageLifeSpace
}

/**
 * One-shot redirect for tabs still pointing at the legacy `/tasks` path.
 * Rewrites the tab's path to `/<lifeSpace>/dormant`, auto-renames the title
 * if it matches one of the known pre-refactor labels (Im Blick / Bereit /
 * English equivalents), and syncs the URL when this tab is the active one.
 * After the tab state flips, the next render of the parent TabContent
 * matches DORMANT_RE and mounts DormantPage.
 */
const LegacyTasksRedirect: React.FC<LegacyTasksRedirectProps> = ({ tab, lifeSpace }) => {
  const { activeTabId } = useActiveTab()
  const { updateTab } = useTabActions()
  const navigate = useNavigate()
  const { t } = useTranslation()

  useEffect(() => {
    const newPath = `/home/${lifeSpace}/dormant`
    const updates: Partial<Tab> = { path: newPath }
    if (LEGACY_TASKS_AUTO_RENAME_TITLES.has(tab.title)) {
      updates.title = t('sidebar.dormant')
      updates.icon = DormantIcon as LucideIcon
    }
    updateTab(tab.id, updates)
    if (tab.id === activeTabId) {
      navigate(newPath, { replace: true })
    }
    // One-shot on mount: intentionally no deps beyond the tab id, so a tab
    // that somehow re-enters this render path doesn't loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab.id])

  return null
}

// Runtime type-guard — the Tab interface types `lifeSpace` as plain `string`
// (covers values like `'all'` that some pages don't accept), so we validate
// before forwarding to lifespace-scoped page components.
const isPageLifeSpace = (value: unknown): value is PageLifeSpace =>
  value === 'personal' || value === 'work' || value === 'home'

interface TabContentProps {
  tab: Tab
}

/**
 * Resolves `tab.path` to the matching page component. Mirrors the route
 * table that used to live in `HomeLayout`, but as a plain function so every
 * tab can be rendered in parallel (outside the `<Routes>` tree).
 *
 * Page components that need to know whether their tab is currently visible
 * (`QuickNotesPage`, `UpcomingPage`, `ProjectPage`, etc.) call
 * `useIsActiveTab()` from `ActiveTabContext` — the previous `isActive`
 * prop would trigger a full cross-tab re-render cascade on every tab
 * switch (TabContent couldn't memo-bail on isActive flips).
 */
export const TabContent: React.FC<TabContentProps> = React.memo(({ tab }) => {
  const { path } = tab
  const lifeSpace: PageLifeSpace = isPageLifeSpace(tab.lifeSpace) ? tab.lifeSpace : 'personal'

  // Path matchers — ordered from most specific to least specific.

  // Overview
  if (path === '/home/overview' || path === '/home') {
    return <Overview />
  }

  // Today (legacy per-space)
  if (path === '/home/personal/today') return <PersonalToday />
  if (path === '/home/work/today') return <WorkToday />

  // Task views (legacy generic)
  if (path === '/home/tasks/today') return <TaskTodayView />
  if (path === '/home/tasks/week') return <TaskWeekView />

  // Legacy "Im Blick" path — redirect to Ruhend (Dormant).
  if (LEGACY_TASKS_RE.test(path)) return <LegacyTasksRedirect tab={tab} lifeSpace={lifeSpace} />

  // Ruhend (Dormant) — paused tasks
  if (DORMANT_RE.test(path)) return <DormantPage lifeSpace={lifeSpace} />

  // Quick Notes
  if (QUICK_NOTES_RE.test(path)) return <QuickNotesPage lifeSpace={lifeSpace} />

  // Upcoming
  if (UPCOMING_RE.test(path)) return <UpcomingPage lifeSpace={lifeSpace} />

  // Goals (Ziele) — all three life spaces
  if (GOALS_RE.test(path)) return <GoalsView lifeSpace={lifeSpace} />

  // Personal features
  if (path === '/home/personal/habits') return <HabitsView lifeSpace="personal" />
  if (path === '/home/personal/recurring') return <RecurringView lifeSpace="personal" />

  // Tutorial project (must precede generic `/area/:areaName`)
  if (TUTORIAL_RE.test(path)) return <TutorialProjectPage />

  // Project pages — projectId is embedded in the path; extract and pass as
  // prop so we don't depend on `useParams` (which only fires inside a
  // matched Route).
  const projectMatch = path.match(PROJECT_RE)
  if (projectMatch) {
    return <ProjectPage lifeSpace={lifeSpace} projectId={projectMatch[2]} tab={tab} />
  }

  // Area pages
  const areaMatch = path.match(AREA_RE)
  if (areaMatch) {
    return <AreaView lifeSpace={lifeSpace} areaName={areaMatch[2]} />
  }

  // Unknown path — warn in dev and render Overview as a safe fallback.
  if (process.env.NODE_ENV !== 'production') {
    console.warn('[TabContent] Unknown tab path, falling back to Overview:', path)
  }
  return <Overview />
})

TabContent.displayName = 'TabContent'
